import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import htmlIcon from './images/HTML5.png';
import cssIcon from './images/css.png';
import jsIcon from './images/js.png';
import reactIcon from './images/react.png';
import phpIcon from './images/php.png';
import djangoIcon from './images/Django.png';
import cIcon from './images/csharp.png';
import pythonIcon from './images/python.png';
import adobeIcon from './images/adobe.png';
import vscodeIcon from './images/vscode.png';
import vsIcon from './images/vs.png';
import eclipseIcon from './images/eclipse.png';
import androidIcon from './images/android.png';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const skillsData = [
  {
    id: 'frontend',
    title: 'Frontend',
    icon: '🖥️',
    skills: [
      { name: 'HTML5', icon: htmlIcon },
      { name: 'CSS3', icon: cssIcon },
      { name: 'JavaScript', icon: jsIcon },
      { name: 'React', icon: reactIcon },
      { name: 'Next.js', emoji: '▲' },
      { name: 'Tailwind CSS', emoji: '🎨' },
    ],
  },
  {
    id: 'backend',
    title: 'Backend',
    icon: '⚙️',
    skills: [
      { name: 'PHP', icon: phpIcon },
      { name: 'Django', icon: djangoIcon },
      { name: 'Python', icon: pythonIcon },
      { name: 'C#', icon: cIcon },
      { name: 'MySQL', emoji: '🗄️' },
      { name: 'PostgreSQL', emoji: '🐘' },
      { name: 'MySQL Workbench', emoji: '🛠️' },
    ],
  },
  {
    id: 'design',
    title: 'Design Tools',
    icon: '🎨',
    skills: [
      { name: 'Adobe Photoshop', icon: adobeIcon },
      { name: 'Canva', emoji: '✏️' },
    ],
  },
  {
    id: 'devtools',
    title: 'Dev Tools',
    icon: '🛠️',
    skills: [
      { name: 'VS Code', icon: vscodeIcon },
      { name: 'Visual Studio', icon: vsIcon },
      { name: 'Eclipse', icon: eclipseIcon },
      { name: 'Android Studio', icon: androidIcon },
    ],
  },
  {
    id: 'networking',
    title: 'Hardware & Networking',
    icon: '🔌',
    skills: [
      { name: 'LAN/WAN Setup' },
      { name: 'Network Cabling' },
      { name: 'Router & Switch Config' },
      { name: 'System Diagnostics' },
      { name: 'Hardware Maintenance' },
      { name: 'Printer & Peripheral Setup' },
    ],
  },
];

const SkillItem = ({ skill }) => {
  if (skill.icon) {
    return (
      <motion.div
        whileHover={{ scale: 1.12, y: -4 }}
        transition={{ type: 'spring', stiffness: 300 }}
        className="flex flex-col items-center gap-2 group"
      >
        <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] group-hover:border-cyan-500/40 group-hover:bg-cyan-500/10 transition-all duration-300 flex items-center justify-center p-2">
          <img src={skill.icon} alt={skill.name} className="w-full h-full object-contain" />
        </div>
        <span className="text-xs text-slate-500 group-hover:text-cyan-400 transition-colors text-center leading-tight">
          {skill.name}
        </span>
      </motion.div>
    );
  }

  if (skill.emoji) {
    return (
      <motion.div
        whileHover={{ scale: 1.12, y: -4 }}
        transition={{ type: 'spring', stiffness: 300 }}
        className="flex flex-col items-center gap-2 group"
      >
        <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] group-hover:border-cyan-500/40 group-hover:bg-cyan-500/10 transition-all duration-300 flex items-center justify-center text-2xl">
          {skill.emoji}
        </div>
        <span className="text-xs text-slate-500 group-hover:text-cyan-400 transition-colors text-center leading-tight">
          {skill.name}
        </span>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all duration-300 group"
    >
      <span className="text-cyan-400 text-xs">✦</span>
      <span className="text-slate-400 text-xs group-hover:text-slate-200 transition-colors">
        {skill.name}
      </span>
    </motion.div>
  );
};

const Skills = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = canvas.parentElement.offsetHeight;

    const stars = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.2 + 0.3,
      opacity: Math.random() * 0.4 + 0.1,
      speed: Math.random() * 0.3 + 0.1,
    }));

    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        s.y -= s.speed;
        if (s.y < 0) {
          s.y = canvas.height;
          s.x = Math.random() * canvas.width;
        }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(6, 182, 212, ${s.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="relative py-32 px-6 bg-[#0d1427] overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none opacity-60" />

      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto">

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-4"
        >
          <span className="font-mono text-cyan-400 text-sm">03.</span>
          <h2 className="text-3xl md:text-4xl font-black text-white">Skills</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-slate-400 mb-16 max-w-xl"
        >
          Technologies and tools I work with on a daily basis.
        </motion.p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillsData.map((category, i) => (
            <motion.div
              key={category.id}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all duration-300 group"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl">{category.icon}</span>
                <h3 className="text-white font-bold text-sm tracking-wide group-hover:text-cyan-400 transition-colors">
                  {category.title}
                </h3>
              </div>

              <div className={`flex flex-wrap gap-4 ${category.id === 'networking' ? 'flex-col gap-2' : ''}`}>
                {category.skills.map((skill) => (
                  <SkillItem key={skill.name} skill={skill} />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
    </div>
  );
};

export default Skills;